import { useCallback, useMemo } from "react";
import { DataStateView } from "@src/components/screen-state";
import { useCameras } from "@src/features/cameras";
import { useSiteNames } from "@src/features/cameras/hooks/useSiteNames";
import { useTrafficFlowScenarios } from "../hooks";
import { groupScenariosBySite, type ScenarioSiteGroup } from "../utils/scenario-group";
import { ScenarioList } from "../components/ScenarioList";

export function TrafficFlowScreenContent() {
  const {
    data: cameras,
    isLoading: isCamerasLoading,
    isFetching: isCamerasFetching,
    error: camerasError,
    refetch: refetchCameras,
  } = useCameras();
  const {
    data: scenarios,
    isLoading: isScenariosLoading,
    isFetching: isScenariosFetching,
    error: scenariosError,
    refetch: refetchScenarios,
    isTenantPending,
  } = useTrafficFlowScenarios();
  const siteNames = useSiteNames();

  const groups = useMemo(
    () => (cameras && scenarios ? groupScenariosBySite(scenarios, cameras, {}, siteNames) : undefined),
    [cameras, scenarios, siteNames],
  );

  const refetch = useCallback(() => {
    void refetchCameras();
    void refetchScenarios();
  }, [refetchCameras, refetchScenarios]);

  return (
    <DataStateView<ScenarioSiteGroup[]>
      data={groups}
      isLoading={isTenantPending || isCamerasLoading || isScenariosLoading}
      isFetching={isCamerasFetching || isScenariosFetching}
      error={camerasError ?? scenariosError ?? null}
      refetch={refetch}
      isEmpty={(g) => g.reduce((sum, group) => sum + group.rows.length, 0) === 0}
      loadingMessage="Loading scenarios..."
      errorTitle="Unable to load scenarios"
      emptyState={{
        icon: "motion",
        title: "No crossline mapping scenarios reported yet",
        message: "Cameras configured for crossline mapping will appear here once they publish scenario status.",
      }}
    >
      {(g) => <ScenarioList groups={g} />}
    </DataStateView>
  );
}
